import React from "react";
import { motion } from "framer-motion";
import { Bath, Droplets, ShieldCheck, Wind } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SHOP_PHOTO_TALL } from "@/i18n/translations";

const ICONS = [Bath, Droplets, ShieldCheck, Wind];

export const About = () => {
  const { t } = useLang();
  const a = t.about;

  return (
    <section id="about" className="relative py-24 sm:py-32" data-testid="about">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_70px_rgba(42,37,34,0.2)] border-4 border-white">
              <img src={SHOP_PHOTO_TALL} alt="Care For Me bamboo spa interior" className="w-full h-[560px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-4 hidden sm:block rounded-2xl bg-bamboo text-brand-cream px-6 py-5 shadow-xl max-w-[210px]">
              <p className="font-heading text-2xl font-semibold leading-tight italic">{a.why_title}</p>
            </div>
            <span className="absolute -top-4 -left-4 h-16 w-16 rounded-full border-2 border-bamboo/40 animate-sway hidden sm:block" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-sm font-bold uppercase tracking-[0.2em] text-bamboo">{a.tag}</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-ink tracking-tight leading-[1.05]">{a.title}</h2>
            <p className="mt-6 text-brand-muted leading-relaxed text-lg">{a.p1}</p>
            <p className="mt-4 text-brand-muted leading-relaxed">{a.p2}</p>
            <div className="mt-7 rounded-2xl bg-bamboo-soft border border-bamboo/15 p-6">
              <p className="text-brand-ink/85 leading-relaxed"><span className="font-bold text-bamboo-deep">{a.why_title}. </span>{a.why_text}</p>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {a.features.map((f, i) => {
                const Icon = ICONS[i];
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.07 }}
                    className="flex items-start gap-3"
                    data-testid={`about-feature-${i}`}
                  >
                    <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-soft text-brand shrink-0">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-brand-ink leading-tight">{f.t}</h3>
                      <p className="text-sm text-brand-muted mt-0.5">{f.d}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
